import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Subject, takeUntil, firstValueFrom } from 'rxjs';
import { AuthProvider, isDebugMode, isProviderEnabled } from '../../config/sso.config';
import { UserProfile } from '../models';
import { AppleAuthService, AppleUser } from './apple-auth.service';
import { GlobalAuthStateService } from './global-auth-state.service';
import { GoogleAuthService, GoogleUser } from './google-auth.service';
import { LoggerService } from './logger.service';
import { MicrosoftAuthService, MicrosoftUser } from './microsoft-auth.service';

/**
 * Unified user profile interface
 *
 * Common interface that normalizes user data across different authentication providers.
 */
export interface UnifiedUser {
  id: string;
  email: string;
  name: string;
  displayName: string;
  provider: AuthProvider;
  avatar?: string;
  originalProfile: MicrosoftUser | GoogleUser | AppleUser;
}

/**
 * Multi-Provider Authentication Service
 *
 * Centralized authentication service that manages multiple SSO providers through
 * dedicated provider services. Provides a unified interface for authentication
 * across Microsoft Azure AD, Google OAuth 2.0, and Apple Sign In.
 *
 * Key Features:
 * - Unified interface for multiple authentication providers
 * - Provider-specific services for optimal implementation
 * - Reactive authentication state management
 * - Automatic provider detection and routing
 * - Consistent user experience across all providers
 * - Enable/disable providers via configuration
 *
 * Architecture:
 * - MicrosoftAuthService: Handles Azure AD/MSAL authentication
 * - GoogleAuthService: Handles Google OAuth 2.0 authentication
 * - AppleAuthService: Handles Apple Sign In authentication
 * - AuthService: Orchestrates all providers with unified interface
 *
 * Usage Examples:
 * ```typescript
 * // Check authentication status (any provider)
 * if (this.authService.isAuthenticated) {
 *   // User is logged in with any provider
 * }
 *
 * // Provider-specific login
 * await this.authService.loginMicrosoft();
 * await this.authService.loginGoogle();
 * await this.authService.loginApple();
 *
 * // Unified logout (auto-detects provider)
 * await this.authService.logout();
 *
 * // Get current user (unified format)
 * const user = this.authService.getCurrentUser();
 * this.logger.info(`${user.displayName} via ${user.provider}`);
 * ```
 *
 * @injectable
 * @providedIn 'root'
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly microsoftAuthService = inject(MicrosoftAuthService);
  private readonly googleAuthService = inject(GoogleAuthService);
  private readonly appleAuthService = inject(AppleAuthService);
  private readonly globalAuthState = inject(GlobalAuthStateService);
  private readonly logger = inject(LoggerService);

  private readonly destroy$ = new Subject<void>();
  private readonly _isAuthenticated = new BehaviorSubject<boolean>(false);
  private readonly _userProfile = new BehaviorSubject<UnifiedUser | null>(null);
  private readonly _currentProvider = new BehaviorSubject<AuthProvider | null>(null);

  /** Observable stream of authentication state changes across all providers */
  readonly isAuthenticated$ = this._isAuthenticated.asObservable();

  /** Observable stream of unified user profile changes */
  readonly userProfile$ = this._userProfile.asObservable();

  /** Observable stream of current authentication provider changes */
  readonly currentProvider$ = this._currentProvider.asObservable();

  constructor() {
    this.initializeProviderListeners();
    this.checkInitialAuthState();
  }

  /**
   * Initialize listeners for all authentication providers
   *
   * Sets up subscriptions to authentication state changes from all provider services
   * to maintain unified authentication state across the application.
   *
   * @private
   */
  private initializeProviderListeners(): void {
    // Listen to Microsoft authentication state changes
    if (isProviderEnabled('microsoft')) {
      this.microsoftAuthService.isAuthenticated$
        .pipe(takeUntil(this.destroy$))
        .subscribe((isAuth) => {
          if (isAuth && this.microsoftAuthService.getCurrentUser()) {
            this.setUnifiedUser(this.microsoftAuthService.getCurrentUser()!, 'microsoft');
          } else if (!isAuth && this._currentProvider.value === 'microsoft') {
            this.clearAuthenticationState();
          }
        });
    }

    // Listen to Google authentication state changes
    if (isProviderEnabled('google')) {
      this.googleAuthService.isAuthenticated$.pipe(takeUntil(this.destroy$)).subscribe((isAuth) => {
        if (isAuth && this.googleAuthService.getCurrentUser()) {
          this.setUnifiedUser(this.googleAuthService.getCurrentUser()!, 'google');
        } else if (!isAuth && this._currentProvider.value === 'google') {
          this.clearAuthenticationState();
        }
      });
    }

    // Listen to Apple authentication state changes
    if (isProviderEnabled('apple')) {
      this.appleAuthService.isAuthenticated$.pipe(takeUntil(this.destroy$)).subscribe((isAuth) => {
        if (isAuth && this.appleAuthService.getCurrentUser()) {
          this.setUnifiedUser(this.appleAuthService.getCurrentUser()!, 'apple');
        } else if (!isAuth && this._currentProvider.value === 'apple') {
          this.clearAuthenticationState();
        }
      });
    }

    if (isDebugMode()) {
      this.logger.debug('Auth Service: Provider listeners initialized');
    }
  }

  /**
   * Check initial authentication state across all providers
   *
   * Determines if the user is already authenticated with any provider
   * when the service initializes (e.g., on page refresh).
   *
   * @private
   */
  private checkInitialAuthState(): void {
    // For Microsoft, we need to wait for MSAL initialization before checking auth state
    if (isProviderEnabled('microsoft')) {
      this.microsoftAuthService
        .initialize()
        .then(() => {
          if (this.microsoftAuthService.isAuthenticated) {
            const user = this.microsoftAuthService.getCurrentUser();
            if (user) {
              this.setUnifiedUser(user, 'microsoft');

              if (isDebugMode()) {
                this.logger.debug('Auth Service: Microsoft auth state restored on page refresh');
              }
              return;
            }
          }
          this.checkOtherProviders();
        })
        .catch((error) => {
          this.logger.error('Auth Service: Microsoft initialization failed:', error);
          this.checkOtherProviders();
        });
    } else {
      this.checkOtherProviders();
    }
  }

  /**
   * Check authentication state for non-Microsoft providers
   *
   * @private
   */
  private checkOtherProviders(): void {
    // Check Google authentication
    if (isProviderEnabled('google') && this.googleAuthService.isAuthenticated) {
      const user = this.googleAuthService.getCurrentUser();
      if (user) {
        this.setUnifiedUser(user, 'google');
        return;
      }
    }

    // Check Apple authentication
    if (isProviderEnabled('apple') && this.appleAuthService.isAuthenticated) {
      const user = this.appleAuthService.getCurrentUser();
      if (user) {
        this.setUnifiedUser(user, 'apple');
        return;
      }
    }

    if (isDebugMode()) {
      this.logger.debug('Auth Service: No initial authentication state found');
    }
  }

  /**
   * Set unified user profile and authentication state
   *
   * Converts provider-specific user data to unified format and updates service state.
   *
   * @param userData - User data from any authentication provider
   * @param provider - The authentication provider
   * @private
   */
  private setUnifiedUser(
    userData: MicrosoftUser | GoogleUser | AppleUser,
    provider: AuthProvider,
  ): void {
    const unifiedUser: UnifiedUser = this.createUnifiedUser(userData, provider);

    this._userProfile.next(unifiedUser);
    this._currentProvider.next(provider);
    this._isAuthenticated.next(true);

    // Update global auth state for Microsoft provider
    if (provider === 'microsoft') {
      // Convert UnifiedUser to UserProfile format expected by GlobalAuthStateService
      const userProfile: UserProfile = {
        id: parseInt(unifiedUser.id, 10) || 0, // Convert string ID to number
        username: unifiedUser.email, // Use email as username
        email: unifiedUser.email,
        firstName: unifiedUser.name.split(' ')[0] || unifiedUser.displayName,
        lastName: unifiedUser.name.split(' ').slice(1).join(' ') || undefined,
      };

      this.globalAuthState.setMicrosoftAuthState({
        isAuthenticated: true,
        user: userProfile,
      });
    }

    if (isDebugMode()) {
      this.logger.debug(`Auth Service: User authenticated via ${provider}:`, unifiedUser);
    }
  }

  /**
   * Clear authentication state for all providers
   *
   * Resets all authentication-related state observables.
   *
   * @private
   */
  private clearAuthenticationState(): void {
    this._isAuthenticated.next(false);
    this._userProfile.next(null);
    this._currentProvider.next(null);

    // Also clear the global authentication cache so other services know user is logged out
    this.globalAuthState.clearAuthState();

    if (isDebugMode()) {
      this.logger.debug('Auth Service: Authentication state cleared');
    }
  }

  /**
   * Create unified user profile from provider-specific data
   *
   * Normalizes user data across different providers into a common format.
   *
   * @param userData - Provider-specific user data
   * @param provider - The authentication provider
   * @returns Unified user profile
   * @private
   */
  private createUnifiedUser(
    userData: MicrosoftUser | GoogleUser | AppleUser,
    provider: AuthProvider,
  ): UnifiedUser {
    switch (provider) {
      case 'microsoft':
        const msalUser = userData as MicrosoftUser;
        return {
          id: msalUser.localAccountId,
          email: msalUser.username,
          name: msalUser.name || '',
          displayName: msalUser.name || msalUser.username,
          provider: 'microsoft',
          originalProfile: msalUser,
        };

      case 'google':
        const googleUser = userData as GoogleUser;
        return {
          id: googleUser.id,
          email: googleUser.email,
          name: googleUser.name,
          displayName: googleUser.name || googleUser.email,
          provider: 'google',
          avatar: googleUser.picture,
          originalProfile: googleUser,
        };

      case 'apple':
        const appleUser = userData as AppleUser;
        return {
          id: appleUser.id,
          email: appleUser.email,
          name: appleUser.name,
          displayName: appleUser.name || appleUser.email || 'Apple User',
          provider: 'apple',
          originalProfile: appleUser,
        };

      default:
        throw new Error(`Unsupported authentication provider: ${provider}`);
    }
  }

  /**
   * Microsoft Azure AD authentication
   *
   * Delegates to MicrosoftAuthService for Azure AD authentication and updates global state.
   *
   * @returns Promise that resolves once redirect is initiated
   */
  async loginMicrosoft(options?: { returnUrl?: string }): Promise<void> {
    if (!isProviderEnabled('microsoft')) {
      throw new Error('Microsoft authentication is disabled');
    }

    try {
      if (isDebugMode()) {
        this.logger.debug('Auth Service: Starting Microsoft login');
      }

      // Optionally persist return URL for post-login navigation
      if (options?.returnUrl) {
        sessionStorage.setItem('starter-post-login-redirect', options.returnUrl);
      }

      // Ensure MSAL is ready before initiating redirect
      await this.microsoftAuthService.initialize();
      this.microsoftAuthService.signInWithRedirect();
    } catch (error) {
      if (isDebugMode()) {
        this.logger.error('Auth Service: Microsoft login failed:', error);
      }
      throw error;
    }
  }

  /**
   * Clear Microsoft authentication data
   *
   * Clears all Microsoft authentication state and cached tokens.
   * Useful for resolving authentication issues.
   */
  async clearMicrosoftAuthenticationData(): Promise<void> {
    try {
      await this.microsoftAuthService.clearAllAuthenticationData();
      this.globalAuthState.clearAuthState();

      if (isDebugMode()) {
        this.logger.debug('Auth Service: Microsoft authentication data cleared');
      }
    } catch (error) {
      this.logger.error('Auth Service: Error clearing Microsoft authentication data:', error);
      throw error;
    }
  }

  /**
   * Google OAuth 2.0 authentication
   *
   * Delegates to GoogleAuthService for Google OAuth authentication.
   *
   * @returns Promise that resolves once redirect is initiated
   */
  async loginGoogle(options?: { returnUrl?: string }): Promise<void> {
    if (!isProviderEnabled('google')) {
      throw new Error('Google authentication is disabled');
    }

    try {
      if (isDebugMode()) {
        this.logger.debug('Auth Service: Starting Google login');
      }

      if (options?.returnUrl) {
        sessionStorage.setItem('starter-post-login-redirect', options.returnUrl);
      }

      await this.googleAuthService.signInWithRedirect();
    } catch (error) {
      if (isDebugMode()) {
        this.logger.error('Auth Service: Google login failed:', error);
      }
      throw error;
    }
  }

  /**
   * Apple Sign In authentication
   *
   * Delegates to AppleAuthService for Apple Sign In authentication.
   *
   * @returns Promise that resolves once redirect is initiated
   */
  async loginApple(options?: { returnUrl?: string }): Promise<void> {
    if (!isProviderEnabled('apple')) {
      throw new Error('Apple authentication is disabled');
    }

    try {
      if (isDebugMode()) {
        this.logger.debug('Auth Service: Starting Apple login');
      }

      if (options?.returnUrl) {
        sessionStorage.setItem('starter-post-login-redirect', options.returnUrl);
      }

      this.appleAuthService.signInWithRedirect();
    } catch (error) {
      if (isDebugMode()) {
        this.logger.error('Auth Service: Apple login failed:', error);
      }
      throw error;
    }
  }

  /**
   * Sign out the current user from all providers
   */
  async logout(): Promise<void> {
    const currentProvider = this._currentProvider.value;

    try {
      if (isDebugMode()) {
        this.logger.debug(`Auth Service: Starting logout for provider: ${currentProvider}`);
      }

      switch (currentProvider) {
        case 'microsoft':
          if (isProviderEnabled('microsoft')) {
            // Clear unified auth state first so UI updates immediately, even if user navigates back
            this.clearAuthenticationState();
            await firstValueFrom(this.microsoftAuthService.signOut());
            return; // Microsoft redirect will replace page, no further action needed
          }
          break;
        case 'google':
          if (isProviderEnabled('google')) {
            await this.googleAuthService.signOut();
          }
          break;
        case 'apple':
          if (isProviderEnabled('apple')) {
            await this.appleAuthService.signOut();
          }
          break;
        default:
          this.clearAuthenticationState();
          return;
      }

      // For non-Microsoft providers, clear unified auth state after logout completes
      this.clearAuthenticationState();

      if (isDebugMode()) {
        this.logger.debug('Auth Service: Logout completed successfully');
      }
    } catch (error: any) {
      if (isDebugMode()) {
        this.logger.error('Auth Service: Logout failed:', error);
      }

      // Check if this is a user cancellation error
      if (this.isUserCancellationError(error)) {
        if (isDebugMode()) {
          this.logger.debug(
            'Auth Service: User cancelled logout, maintaining authentication state',
          );
        }
        throw error; // Re-throw without clearing state
      }

      // For other errors, clear authentication state
      this.clearAuthenticationState();
      throw error;
    }
  }

  /**
   * Check if the error indicates user cancelled the logout
   */
  private isUserCancellationError(error: any): boolean {
    if (!error) return false;

    // MSAL and general error codes for user cancellation
    const cancellationErrorCodes = [
      'user_cancelled',
      'UserCancelledPopupError',
      'BrowserAuthError: popup_closed_by_user',
      'popup_closed_by_user',
      'popup_window_error',
      'user_cancelled_error',
      'interaction_in_progress',
      'error_user_cancel',
      'cancelled_by_user',
    ];

    const errorMessage = error?.message || error?.errorCode || error?.code || error?.name || '';
    const errorString = errorMessage.toString().toLowerCase();

    return cancellationErrorCodes.some((code) => errorString.includes(code.toLowerCase()));
  }

  /**
   * Get the currently authenticated user (unified across all providers)
   */
  getCurrentUser(): UnifiedUser | null {
    return this._userProfile.value;
  }

  /**
   * Get the current authentication provider
   */
  getCurrentProvider(): AuthProvider | null {
    return this._currentProvider.value;
  }

  /**
   * Check if a user is currently authenticated (unified across all providers)
   */
  get isAuthenticated(): boolean {
    return this._isAuthenticated.value;
  }

  /**
   * Get the display name of the current user (unified across all providers)
   */
  getUserDisplayName(): string {
    const user = this.getCurrentUser();
    return user?.displayName || 'User';
  }

  /**
   * Get the email address of the current user (unified across all providers)
   */
  getUserEmail(): string {
    const user = this.getCurrentUser();
    return user?.email || '';
  }

  /**
   * Get provider-specific user data
   */
  getOriginalProfile(): MicrosoftUser | GoogleUser | AppleUser | null {
    const user = this.getCurrentUser();
    return user?.originalProfile || null;
  }

  /**
   * Check if a specific provider is currently active
   */
  isProviderActive(provider: AuthProvider): boolean {
    return this._currentProvider.value === provider;
  }

  /**
   * Get access token for API calls (Microsoft only)
   */
  async getAccessToken(scopes: string[] = []): Promise<string | null> {
    const currentProvider = this._currentProvider.value;

    if (currentProvider === 'microsoft' && isProviderEnabled('microsoft')) {
      try {
        const token = await firstValueFrom(this.microsoftAuthService.getAccessToken(scopes));
        return token || null;
      } catch (error) {
        if (isDebugMode()) {
          this.logger.error('Auth Service: Failed to get access token:', error);
        }
        return null;
      }
    }

    return null;
  }

  /**
   * Backward compatibility methods
   */

  /**
   * @deprecated Use loginMicrosoft() instead
   */
  async loginPopup(): Promise<any> {
    this.logger.warn('AuthService.loginPopup() is deprecated. Use loginMicrosoft() instead.');
    return this.loginMicrosoft();
  }

  /**
   * @deprecated Use logout() instead
   */
  async logoutPopup(): Promise<void> {
    this.logger.warn('AuthService.logoutPopup() is deprecated. Use logout() instead.');
    return this.logout();
  }

  /**
   * @deprecated Use getCurrentUser() instead
   */
  getActiveAccount(): any {
    this.logger.warn('AuthService.getActiveAccount() is deprecated. Use getCurrentUser() instead.');
    const user = this.getCurrentUser();
    return user?.provider === 'microsoft' ? user.originalProfile : null;
  }

  /**
   * Clean up service resources and subscriptions
   */
  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.microsoftAuthService.destroy();
  }
}
